/**
 * Brand Rep signup + success experience.
 *
 * Two submission paths, chosen at runtime by whether the section's
 * `api_endpoint` setting is configured:
 *
 *  - ENDPOINT CONFIGURED (real backend deployed — see the
 *    ancarta-brand-rep-backend project): fetch()-based transaction against
 *    the real signup API. Only a `{ success: true }` response transforms
 *    the panel into the CONFIRMED success state with her real code. Any
 *    failure shows an inline error and lets her retry — never a fake
 *    success.
 *  - ENDPOINT BLANK (today's reality — nothing is deployed yet): the form
 *    submits natively, exactly as before. See the architecture note at the
 *    top of sections/brand-rep-signup.liquid for why that path exists and
 *    what it can/can't do. It shows the PENDING "Activating" state, never
 *    the confirmed one — the two states are visually and textually
 *    distinct (see showConfirmedSuccess vs hydratePendingSuccessState).
 */

const RESERVED_CODES = [
  'ADMIN', 'ANCARTA', 'OWNER', 'STAFF', 'TEAM', 'SUPPORT', 'WELCOME',
  'WELCOME20', 'SALE', 'FREE', 'DISCOUNT', 'TEST', 'NULL', 'UNDEFINED',
  'RUBY', 'BRANDREP', 'BRAND', 'REP', 'SHOPIFY', 'CODE',
];

/**
 * Presentation strings (FR/DE i18n, 2026-09-29). The signup sections render
 * each localised string into a data attribute on a hidden
 * [data-ancarta-i18n="brand_rep"] element (`data-t-<key>`, via Liquid `| t`,
 * decoded by the HTML parser). The CURRENT English literal is always passed
 * as the fallback, so behaviour is unchanged when the element or a key is
 * missing. `{name}`-style tokens are filled from `vars`. Display only —
 * never used in any comparison or business rule.
 */
let i18nElement;
function t(key, fallback, vars) {
  if (i18nElement === undefined) i18nElement = document.querySelector('[data-ancarta-i18n="brand_rep"]');
  const value = i18nElement ? i18nElement.getAttribute('data-t-' + key) : null;
  let text = value || fallback;
  if (vars) {
    Object.keys(vars).forEach((name) => {
      text = text.split('{' + name + '}').join(vars[name]);
    });
  }
  return text;
}

// Upper bound for one signup POST (request + response body) before the
// button is re-enabled with a retryable error. See submitToRealBackend.
// Longer than the backend's 25s /signup response budget, so the backend's own
// clean 'timeout' reply normally arrives before the browser gives up.
const SIGNUP_REQUEST_TIMEOUT_MS = 30000;

const SESSION_KEY = 'ancartaBrandRepSubmission';
// Ruby welcome screen (2026-10-20): her confirmed welcome details, kept on HER device so a refresh
// (or reopening Ruby's link) shows her welcome screen again instead of an empty signup form.
// Only what the screen shows: first name, 15% share code, private offer code. No time limit (owner
// 2026-10-20: girls come back a week or more later, e.g. on pay day); "Not you? Start again" clears it.
const WELCOME_KEY = 'ancartaWelcome';
// Brand Rep signup page (owner 2026-10-28): the same for girls who sign up as a Brand Rep, under its own key so a
// Ruby welcome and a Brand Rep welcome never show on each other's page. Same fields; "Start a new signup" clears it.
const BR_WELCOME_KEY = 'ancartaBrandRepWelcome';

/** "Not you?" / sign out: forget her welcome on this device (shared phones). */
function clearSavedWelcome() {
  try {
    localStorage.removeItem(WELCOME_KEY);
    localStorage.removeItem(BR_WELCOME_KEY);
    sessionStorage.removeItem('ancartaBrandRepContext');
    sessionStorage.removeItem('ancartaLook');
  } catch (error) {
    /* ignore */
  }
}

function readSavedWelcome() {
  try {
    const saved = JSON.parse(localStorage.getItem(WELCOME_KEY) || 'null');
    if (!saved || !saved.privateBogoCode) {
      localStorage.removeItem(WELCOME_KEY);
      return null;
    }
    return saved;
  } catch (error) {
    return null;
  }
}

/**
 * Brand Rep signup page (owner 2026-10-28): her welcome from HER signed-in customer record, rendered by Liquid in
 * sections/brand-rep-signup.liquid ([data-br-account-welcome]) only when she is signed in as an active rep. Needs her
 * share code; her private code is shown when she has one. A read of existing data: nothing is created or sent.
 */
function readBrandRepAccountWelcome() {
  const el = document.querySelector('script[data-br-account-welcome]');
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent || 'null');
    return data && data.code ? data : null;
  } catch (error) {
    return null;
  }
}

/** Brand Rep signup page (owner 2026-10-28): her saved Brand Rep welcome, which needs at least her share code. */
function readSavedBrandRepWelcome() {
  try {
    const saved = JSON.parse(localStorage.getItem(BR_WELCOME_KEY) || 'null');
    if (!saved || !saved.code) {
      localStorage.removeItem(BR_WELCOME_KEY);
      return null;
    }
    return saved;
  } catch (error) {
    return null;
  }
}
/**
 * Returning Brand Rep (owner-approved 2026-10-21): her welcome details as rendered by Liquid from HER signed-in
 * Shopify customer record (sections/unlock-signup.liquid, [data-rep-account-welcome]). Present only when she is
 * signed in as an active rep with an invitation code. A read of existing data: nothing is created or sent.
 */
function readAccountWelcome() {
  const el = document.querySelector('script[data-rep-account-welcome]');
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent || 'null');
    return data && data.code && data.privateBogoCode ? data : null;
  } catch (error) {
    return null;
  }
}

/** Shopify login that returns her to this exact page (path + query, e.g. ?src=ruby). Relative only, never ?signup. */
function loginUrlForThisPage() {
  const params = new URLSearchParams(window.location.search);
  params.delete('signup');
  const query = params.toString();
  const back = window.location.pathname + (query ? '?' + query : '');
  return '/customer_authentication/login?return_to=' + encodeURIComponent(back);
}

const SOURCE_KEY = 'ancartaBrandRepSource';
const ATTRIBUTION_KEY = 'ancartaBrandRepAttribution';

function publish(eventName, payload) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, payload || {});
    }
  } catch (error) {
    /* analytics must never break the funnel */
  }
}

function slugify(value, maxLength) {
  if (!value) return '';
  const cleaned = value
    .toString()
    .trim()
    .replace(/^@+/, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
  return maxLength ? cleaned.slice(0, maxLength) : cleaned;
}

// Instagram / TikTok field (2026-10-21): girls often paste their profile link. Take the
// username out of an instagram.com / tiktok.com URL (dropping ?igsh=… and the trailing /),
// and fold accents (é -> e) instead of deleting the letter. Punctuation-only handles stay
// accepted, as on the backend (validation.js, 2026-09-11).
/**
 * Ruby welcome bio mock-up (owner 2026-10-21): her first name and Instagram username in the phone.
 * The username is the one she typed on the form (profile links reduced to the bare username); a
 * welcome saved before the username was stored falls back to her private code, which is made from
 * her Instagram username. Leaves the placeholders only when nothing is known.
 */
function fillBioMock(panel, firstName, handleSources) {
  if (!panel) return;
  const mockName = panel.querySelector('[data-share-mock-name]');
  if (mockName && firstName) mockName.textContent = firstName;
  const raw = (handleSources || []).find((value) => typeof value === 'string' && value.trim());
  const handle = raw ? socialHandle(raw, 30) : '';   // display only, never a code
  const mockUser = panel.querySelector('[data-share-mock-username]');
  if (mockUser && handle) mockUser.textContent = handle;
}

function socialHandle(value, maxLength) {
  let raw = (value || '').toString().trim();
  const link = raw.match(/(?:instagram\.com|instagr\.am|tiktok\.com)\/+@?([^/?#\s]+)/i);
  if (link) raw = link[1];
  raw = raw.normalize ? raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : raw;
  return slugify(raw, maxLength);
}

function sanitizeCode(value) {
  return (value || '')
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 12);
}

/**
 * Alternative suggestions offered on a collision — reserved-word (checked
 * client-side) or code-taken (only ever known via a real backend
 * response). Client-side generation only; never framed as an availability
 * check itself.
 */
function suggestAlternatives(baseCode) {
  const letters = (baseCode || '').replace(/[0-9]/g, '') || 'REP';
  const suffixes = ['G15', 'T15', 'A15', 'X15'];
  const seen = new Set();
  const suggestions = [];
  for (const suffix of suffixes) {
    const candidate = (letters + suffix).slice(0, 12);
    if (!RESERVED_CODES.includes(candidate) && !seen.has(candidate)) {
      seen.add(candidate);
      suggestions.push(candidate);
    }
    if (suggestions.length >= 3) break;
  }
  return suggestions;
}

function captureSource() {
  try {
    const existing = sessionStorage.getItem(SOURCE_KEY);
    if (existing) return existing;
  } catch (error) {
    /* sessionStorage may be unavailable (private mode, disabled) */
  }

  const params = new URLSearchParams(window.location.search);
  let source = params.get('utm_source');

  if (!source && document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      if (/instagram\.com$/.test(referrerHost)) source = 'instagram';
      else if (/tiktok\.com$/.test(referrerHost)) source = 'tiktok';
      else source = referrerHost;
    } catch (error) {
      /* ignore unparsable referrer */
    }
  }

  source = source || 'direct';

  try {
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch (error) {
    /* non-fatal */
  }

  return source;
}

export function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  } catch (error) {
    return '';
  }
}

/**
 * Phase 5B attribution capture (2026-09-06). Additive only — does not
 * change captureSource()'s existing behaviour/callers. Cached in
 * sessionStorage exactly like captureSource(), so the FIRST page load of a
 * session is what gets recorded even if she navigates to other pages
 * (losing the original URL's query string) before finishing the form. Never
 * fabricates a value: every field is either a real, directly-observed
 * string or `null` (meaning unavailable), matching the CAPI_ATTRIBUTION_DESIGN.md
 * / BRAND_REP_EVENT_TAXONOMY.md convention in the ancarta-meta-ads-agent
 * project this capture logic was designed against.
 *
 * Deliberately does NOT attempt to read the Instagram-app in-app-browser's
 * referrer any more reliably than captureSource() already does — that
 * hop is proven (see docs/ATTRIBUTION_AUDIT.md) to carry no ad-level
 * identifier no matter how it's read.
 */
export function captureAttribution() {
  try {
    const cached = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (cached) return JSON.parse(cached);
  } catch (error) {
    /* sessionStorage unavailable or cached value corrupt — recapture below */
  }

  const params = new URLSearchParams(window.location.search);
  const readParam = (name) => params.get(name) || null;

  const fbclid = readParam('fbclid');
  const existingFbc = readCookie('_fbc') || null;
  // Meta's documented fbc construction rule: fb.1.<ms-timestamp>.<fbclid> —
  // only ever built when a real fbclid is present; never fabricated from
  // nothing, and an existing _fbc cookie (set by the Meta Pixel itself, if
  // present on this page) always takes precedence over a freshly-built one.
  const fbc = existingFbc || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : null);

  const attribution = {
    utm_source: readParam('utm_source'),
    utm_medium: readParam('utm_medium'),
    utm_campaign: readParam('utm_campaign'),
    utm_content: readParam('utm_content'),
    utm_term: readParam('utm_term'),
    // Populated only when a future direct-to-page Meta ad uses Dynamic URL
    // Parameters to append these — see CAMPAIGN_ARCHITECTURE_V2.md. Always
    // null for the current Instagram-profile-destination ads, which have no
    // destination URL at all to carry them.
    meta_campaign_id: readParam('meta_campaign_id'),
    meta_adset_id: readParam('meta_adset_id'),
    meta_ad_id: readParam('meta_ad_id'),
    meta_creative_id: readParam('meta_creative_id'),
    fbclid: fbclid,
    fbc: fbc,
    fbp: readCookie('_fbp') || null,
    landing_url: window.location.href,
    referrer: document.referrer || null,
    captured_at: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch (error) {
    /* non-fatal */
  }

  return attribution;
}

/**
 * Locale-aware storefront root ("/", "/fr/", "/de/") for building in-store
 * paths such as /discount/CODE. Falls back to "/" (the previous hard-coded
 * behaviour) when Shopify.routes is unavailable.
 */
function shopRoot() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  if (typeof root !== 'string' || !root.startsWith('/') || root.startsWith('//')) return '/';
  return root.endsWith('/') ? root : root + '/';
}

function generateIdempotencyKey() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    /* fall through to the manual fallback below */
  }
  // Good enough entropy for an idempotency key (not a security token) on
  // browsers without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function buildBioText(firstName, code, shareUrl) {
  var lines = [t('bio-line-1', 'Ancarta Brand Rep'), t('bio-line-2', '15% OFF @ancarta.co —') + ' ' + code];
  // Visible bio text stays link-clean (no https://) — the copy button for
  // the link itself always copies the complete URL, set separately below.
  if (shareUrl) lines.push(shareUrl.replace(/^https:\/\//, ''));
  return lines.join('\n');
}

function initSignupForm() {
  const form = document.getElementById('brand-rep-signup-form');
  if (!form) return;

  // Selected by the shared `data-state` contract, not a class name, so
  // this same script works unmodified on any page that reuses this exact
  // form markup with different presentation classes (e.g. the Unlock
  // funnel's `.unlock__panel` vs this page's `.br-signup__panel`).
  const formPanel = form.querySelector('[data-state="form"]');
  const successPanel = form.querySelector('[data-state="success"]');
  const apiEndpoint = formPanel ? (formPanel.getAttribute('data-api-endpoint') || '').trim() : '';
  // Distinguishes the Unlock funnel (which has its own button copy/refs)
  // from the plain brand-rep-signup.liquid page — detected structurally
  // (the unlockPrivateCode ref only ever exists on the Unlock markup),
  // not by page URL, so this keeps working if either page is renamed.
  const isUnlockFunnel = Boolean(successPanel && successPanel.querySelector('[ref="unlockPrivateCode"]'));

  // The welcome hero is lazy on the form screen (so it doesn't compete with the page on mobile data);
  // start fetching it once she begins the form, so it's ready when the welcome screen opens.
  if (successPanel) {
    const warmWelcome = () => successPanel.querySelectorAll('img.welcome-hero__img[loading="lazy"], img.welcome-value__img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    form.addEventListener('focusin', warmWelcome, { once: true });
  }

  const fullNameInput = form.querySelector('[ref="fullNameInput"]');
  const firstNameInput = form.querySelector('[ref="firstNameInput"]');
  const lastNameInput = form.querySelector('[ref="lastNameInput"]');
  const instagramInput = form.querySelector('[ref="instagramInput"]');
  const emailInput = form.querySelector('[name="contact[email]"]');
  const tiktokInput = form.querySelector('[ref="tiktokInput"]');
  const codeInput = form.querySelector('[ref="codeInput"]');
  const codeStatus = form.querySelector('[ref="codeStatus"]');
  const tagsInput = form.querySelector('[ref="tagsInput"]');
  const termsCheckbox = form.querySelector('[ref="termsCheckbox"]');
  // Ruby's page (2026-10-20): marketing consent is separate from the terms box. There is no marketing box on
  // the form, so it sends marketingOptIn: false. Forms without the attribute send no field (backend keeps their old rule).
  const marketingCheckbox = form.querySelector('[ref="marketingCheckbox"]');
  const separateMarketingConsent = Boolean(form.querySelector('[data-marketing-consent="separate"]'));
  const submitButton = form.querySelector('[ref="submitButton"]');
  // Supports both pages' label class names from one lookup — the two
  // pages never both match, so this is a safe either/or, not a behaviour
  // change for brand-rep-signup.liquid.
  const submitLabel = submitButton ? submitButton.querySelector('.br-signup__submit-label, .unlock__submit-label') : null;
  const submitArrow = submitButton ? submitButton.querySelector('.unlock__submit-arrow') : null;
  const apiErrorMessage = form.querySelector('[ref="apiErrorMessage"]');
  const statusLive = form.querySelector('[ref="statusLive"]');
  const idempotencyKeyInput = form.querySelector('[ref="idempotencyKeyInput"]');

  if (idempotencyKeyInput) idempotencyKeyInput.value = generateIdempotencyKey();

  let formStarted = false;
  let isSubmitting = false;

  const markFormStarted = () => {
    if (formStarted) return;
    formStarted = true;
    publish('brand_rep_form_start', {});
  };

  function announce(message) {
    if (statusLive) statusLive.textContent = message;
  }

  function showCollisionSuggestions(baseValue, message) {
    if (!codeStatus) return;
    const alternatives = suggestAlternatives(baseValue.replace(/[0-9]/g, ''));
    codeStatus.textContent = '';
    codeStatus.setAttribute('data-valid', 'false');
    const label = document.createElement('span');
    label.textContent = message + ' ';
    codeStatus.appendChild(label);
    alternatives.forEach((alt, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'br-signup__code-suggestion';
      button.textContent = alt;
      button.addEventListener('click', () => {
        codeInput.value = alt;
        updateCodeStatus();
        codeInput.focus();
      });
      codeStatus.appendChild(button);
      if (index < alternatives.length - 1) codeStatus.appendChild(document.createTextNode(' '));
    });
  }

  function updateCodeStatus() {
    if (!codeInput || !codeStatus) return;
    const value = codeInput.value;

    if (!value) {
      codeStatus.textContent = '';
      codeStatus.removeAttribute('data-valid');
      return;
    }

    if (value.length < 3) {
      codeStatus.textContent = t('code-min-length', 'Use at least 3 characters.');
      codeStatus.setAttribute('data-valid', 'false');
      return;
    }

    if (RESERVED_CODES.includes(value)) {
      showCollisionSuggestions(value, t('code-reserved', 'That word is reserved — try:'));
      return;
    }

    // Deliberately no positive "available"/"looks good" message here — this
    // is only a client-side format check (length, reserved words), not a
    // real Shopify availability check. Whether the code is actually free
    // is only known once the backend tries to create the real discount at
    // submission time (see submitToRealBackend's code_taken handling).
    codeStatus.textContent = '';
    codeStatus.removeAttribute('data-valid');
  }

  if (fullNameInput) {
    fullNameInput.addEventListener('input', markFormStarted);
  }

  if (codeInput) {
    codeInput.addEventListener('input', () => {
      markFormStarted();
      const cursorAtEnd = codeInput.selectionStart === codeInput.value.length;
      codeInput.value = sanitizeCode(codeInput.value);
      if (cursorAtEnd) codeInput.setSelectionRange(codeInput.value.length, codeInput.value.length);
      updateCodeStatus();
    });
  }

  [instagramInput, tiktokInput].forEach((input) => {
    if (!input) return;
    input.addEventListener('input', markFormStarted);
    input.addEventListener('blur', () => {
      if (!input.value) return;
      const handle = socialHandle(input.value, 30);
      input.value = handle ? '@' + handle : '';
    });
  });

  if (emailInput) emailInput.addEventListener('input', markFormStarted);

  captureSource();

  // Signup progress bar (owner 2026-10-21, Ruby's unlock form only): a timer, not live backend progress. Climbs to ~80%
  // at 10 s and ~88% at 13 s, keeps moving towards 98%, never shows 100% until the signup has really succeeded (finish),
  // and disappears on any error (reset). Captions change as it goes; after 20 s a calm "please keep this page open" line.
  const progress = (() => {
    const root = isUnlockFunnel ? (form.closest('[data-state="form"]') || form).querySelector('[data-unlock-progress]') : null;
    if (!root) return { start() {}, finish() { return Promise.resolve(); }, reset() {} };
    const fill = root.querySelector('[data-unlock-progress-fill]');
    const pct = root.querySelector('[data-unlock-progress-pct]');
    const caption = root.querySelector('[data-unlock-progress-caption]');
    const slow = root.querySelector('[data-unlock-progress-slow]');
    const bar = root.querySelector('[role="progressbar"]');
    // data-step-1..4 (a digit after the dash is not camel-cased into dataset, so read the attributes directly)
    const steps = [0, 4, 8, 12].map((at, i) => [at, root.getAttribute('data-step-' + (i + 1))]).filter((x) => x[1]);
    let frame = null;
    let startedAt = 0;
    // Owner 2026-10-21: real signups take ~12-13 s and it sat at 90-91% for 2-3 s, so it now climbs more slowly and keeps
    // moving: ~80% at 10 s, ~88% at 13 s, ~95% at 20 s, approaching 98% (never 100% until the real success).
    const valueAt = (s) => 98 * (1 - Math.exp(-s / 6));
    const paint = (v) => {
      const whole = Math.floor(v);
      if (fill) fill.style.width = v + '%';
      if (pct) pct.textContent = whole + '%';
      if (bar) bar.setAttribute('aria-valuenow', String(whole));
    };
    const tick = () => {
      const s = (performance.now() - startedAt) / 1000;
      paint(valueAt(s));
      const step = steps.filter((x) => s >= x[0]).pop();
      if (step && caption && caption.textContent !== step[1]) caption.textContent = step[1];
      if (slow) slow.hidden = s < 20;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    };
    return {
      start() {
        stop();
        if (fill) fill.classList.remove('is-finishing');
        root.hidden = false;
        paint(0);
        if (slow) slow.hidden = true;
        startedAt = performance.now();
        frame = requestAnimationFrame(tick);
      },
      finish() {
        if (root.hidden) return Promise.resolve();
        stop();
        if (fill) fill.classList.add('is-finishing');
        paint(100);
        return new Promise((resolve) => setTimeout(resolve, 350));
      },
      reset() {
        stop();
        root.hidden = true;
        if (fill) fill.classList.remove('is-finishing');
        paint(0);
      },
    };
  })();

  // A definitive success completes the bar before the welcome screen; anything else leaves it to setLoading(false).
  async function finishProgressIfSucceeded(response, result) {
    if (response && response.ok && result && result.success === true) await progress.finish();
  }

  function setLoading(loading) {
    isSubmitting = loading;
    if (submitButton) submitButton.disabled = loading;
    if (submitLabel) {
      submitLabel.textContent = isUnlockFunnel
        ? (loading ? t('submit-busy', 'CREATING YOUR PRIVATE OFFER…') : t('submit', 'UNLOCK MY PRIVATE OFFER'))
        : (loading ? t('submit-busy', 'CREATING YOUR BRAND REP PROFILE…') : t('submit', 'CREATE MY BRAND REP PROFILE'));
    }
    // Arrow reads oddly trailing an ellipsis mid-request — purely
    // cosmetic, same disabled/enabled moments as the label above.
    if (submitArrow) submitArrow.hidden = loading;
    if (loading) progress.start();
    else progress.reset();
    if (loading) announce(isUnlockFunnel ? t('submit-busy-status', 'Creating your private offer, please wait.') : t('submit-busy-status', 'Creating your Brand Rep profile, please wait.'));
  }

  function showApiError(message) {
    if (!apiErrorMessage) return;
    apiErrorMessage.textContent = message;
    apiErrorMessage.hidden = false;
    apiErrorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    announce(message);
  }

  function clearApiError() {
    if (!apiErrorMessage) return;
    apiErrorMessage.hidden = true;
    apiErrorMessage.textContent = '';
  }

  function storefrontLocaleAtSubmission() {
    return (formPanel && formPanel.getAttribute('data-storefront-locale')) ||
      (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
  }

  // Her Instagram username as submitted, for the welcome screen's bio mock-up only (owner 2026-10-21).
  let lastSubmittedHandle = '';
  let lastSubmittedFirstName = '';

  function collectPayload() {
    const fullName = fullNameInput ? fullNameInput.value.trim() : '';
    lastSubmittedHandle = instagramInput ? instagramInput.value : '';
    lastSubmittedFirstName = fullName.split(/\s+/)[0] || '';
    return {
      fullName,
      email: emailInput ? emailInput.value.trim() : '',
      instagram: instagramInput ? instagramInput.value : '',
      tiktok: tiktokInput ? tiktokInput.value : '',
      code: codeInput ? sanitizeCode(codeInput.value) : '',
      // Terms acceptance. On the Brand Rep signup this single box also covers Brand Rep emails;
      // on Ruby's page marketing consent is the separate optional box below (backend validation.js).
      termsAccepted: termsCheckbox ? termsCheckbox.checked : false,
      ...(separateMarketingConsent ? { marketingOptIn: Boolean(marketingCheckbox && marketingCheckbox.checked) } : {}),
      source: captureSource(),
      attribution: captureAttribution(),
      preferredLocale: storefrontLocaleAtSubmission(),
    };
  }

  // Backend field errors are English-only. Use the theme's translated message
  // where one exists; keep the backend's English text on English pages, and the
  // translated generic error elsewhere (review fix 2026-10-04).
  function localizedFieldError(e) {
    if (e && e.field === 'code' && e.code === 'reserved') return t('code-reserved', e.message || 'That word is reserved — try:');
    if (e && e.field === 'code' && e.code === 'invalid') return t('code-min-length', e.message || 'Use at least 3 characters.');
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    if (lang === 'en' || lang.startsWith('en-')) return (e && e.message) || t('error-generic', 'Something went wrong creating your Brand Rep profile. Please try again.');
    return t('error-generic', 'Something went wrong creating your Brand Rep profile. Please try again.');
  }

  async function submitToRealBackend(payload) {
    setLoading(true);
    clearApiError();

    // Remediation 2026-10-04: bounded request. Previously a hung connection
    // left the button disabled on "CREATING…" forever. The AbortController
    // covers BOTH the request and reading the response body. On timeout the
    // Idempotency-Key is deliberately NOT rotated (same as the network-error
    // path): the backend may have finished the signup even though no
    // response reached her, so her retry must replay under the same key and
    // receive the cached result instead of creating anything twice. The key
    // is only rotated after a definitive, parsed response (below).
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timedOut = false;
    const timeoutId = controller
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, SIGNUP_REQUEST_TIMEOUT_MS)
      : null;

    let response;
    let data;
    try {
      try {
        response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKeyInput ? idempotencyKeyInput.value : '',
          },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
        });
      } catch (networkError) {
        setLoading(false);
        // A timeout may have succeeded server-side, so it must not use the
        // network message ("nothing has been created yet"); the existing,
        // already-translated neutral "please try again" message is used.
        showApiError(
          timedOut
            ? t('error-parse', 'Something went wrong on our end. Please try again.')
            : t('error-network', 'We couldn’t reach Ancarta right now. Check your connection and try again — nothing has been created yet.')
        );
        return;
      }

      try {
        data = await response.json();
      } catch (parseError) {
        setLoading(false);
        showApiError(t('error-parse', 'Something went wrong on our end. Please try again.'));
        return;
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    await finishProgressIfSucceeded(response, data);   // the bar reaches 100% only on a real success
    setLoading(false);

    // A real response was received and understood — this specific attempt
    // is now fully resolved (success or a definitive error), so the next
    // submit must be treated as a NEW attempt, not a retry of this one.
    // Regenerating here (not in the network-error catch above, where no
    // response was ever received and reusing the same key is what keeps a
    // genuine connectivity-retry safely idempotent) fixes a real bug: the
    // backend caches every definitive response — including errors like
    // code_taken — under the Idempotency-Key header to guard against
    // double-submits. Without this, editing the code (or anything else)
    // after a rejection and resubmitting on the same page load would reuse
    // the same key and the backend would blindly replay the STALE cached
    // response from the earlier, different attempt — e.g. showing
    // "That code has already been taken" for a code that was never
    // actually checked.
    // Exception: 'timeout' (the backend's response budget ran out while the
    // signup keeps running server-side) and 'in_progress' (an earlier attempt
    // with this key is still running) are NOT definitive. Keep the key so her
    // retry replays the real result instead of starting a second signup.
    const nonDefinitive = data && (data.error === 'timeout' || data.error === 'in_progress');
    if (nonDefinitive) {
      showApiError(t('error-parse', 'Something went wrong on our end. Please try again.'));
      return;
    }
    if (idempotencyKeyInput) idempotencyKeyInput.value = generateIdempotencyKey();

    if (response.ok && data && data.success) {
      showConfirmedSuccess(data);
      return;
    }

    // Every failure branch below leaves her entered data in the form and
    // lets her retry — never a fake success.
    if (data && data.error === 'code_taken') {
      // The theme's own translation wins: the backend sends English-only messages.
      showCollisionSuggestions(payload.code, t('error-code-taken', data.message || 'That code has already been taken. Try another:'));
      codeInput.focus();
      return;
    }
    if (data && data.error === 'existing_rep') {
      showApiError(
        t('error-existing-rep', data.message || "It looks like you're already an Ancarta Brand Rep.") +
        (data.code ? ' ' + t('your-code', 'Your code:') + ' ' + data.code : '')
      );
      return;
    }
    if (data && data.error === 'rate_limited') {
      showApiError(t('error-rate-limited', 'Too many attempts — please wait a minute and try again.'));
      return;
    }
    if (data && data.error === 'validation' && Array.isArray(data.fieldErrors)) {
      showApiError(data.fieldErrors.map(localizedFieldError).join(' '));
      return;
    }
    showApiError(t('error-generic', 'Something went wrong creating your Brand Rep profile. Please try again.'));
  }

  // Existing-rep privacy (2026-09-20, DEV): when the backend runs EXISTING_REP_RESPONSE_MODE=redacted it returns
  // { detailsWithheld: true, message } and NO rep data. Render a warm, truthful message and hide every card that would
  // otherwise show an empty/placeholder code. Only text from the backend `message` is used — never rep data.
  function showWithheldRecovery(data) {
    // Ruby's page (fix 2026-10-21): since the 2026-10-20 welcome redesign the greeting sits inside the hero, so the
    // older branch below hid the hero (and this message with it) and left "your code is being prepared" showing,
    // which is untrue for a girl who already joined. Show one clear block instead, with a way back in.
    if (isUnlockFunnel) {
      let block = successPanel.querySelector('[data-withheld-block]');
      if (!block) {
        block = document.createElement('div');
        block.setAttribute('data-withheld-block', '');
        block.className = 'welcome-withheld';
        successPanel.insertBefore(block, successPanel.firstChild);
      }
      successPanel.classList.add('is-withheld');
      const heading = document.createElement('h2');
      heading.className = 'welcome-h3';
      heading.textContent = t('already-rep-greeting', "You're already a Brand Rep");
      const note = document.createElement('p');
      note.className = 'welcome-body';
      note.setAttribute('data-withheld-note', '');
      note.textContent = t('withheld-access-note', 'Nothing new was created. Sign in with your email to see your invitation and codes again. We’ll email you a one-time code.');
      const access = document.createElement('a');
      access.setAttribute('data-withheld-access', '');
      access.setAttribute('data-br-analytics', 'unlock_access_invitation_clicked');
      access.className = 'welcome-button-outline';
      access.href = loginUrlForThisPage();
      access.textContent = t('access-invitation', 'Access your invitation');
      block.replaceChildren(heading, note, access);
      if (formPanel) formPanel.hidden = true;
      successPanel.hidden = false;
      showSuccessFromTop();
      announce(t('withheld-announce', "You're already an Ancarta Brand Rep. Your details are in your welcome email."));
      publish('brand_rep_signup_complete', { source: captureSource(), backend: 'confirmed', recovered: true, withheld: true });
      return;
    }
    const greetingEl = successPanel.querySelector('[ref="successGreeting"]');
    if (greetingEl) greetingEl.textContent = t('already-rep-greeting', "You're already a Brand Rep");
    const intro = greetingEl ? greetingEl.parentElement : null;
    Array.from(successPanel.children).forEach((child) => {
      if (child !== intro) child.hidden = true;
    });
    // The intro block also carries the "welcome / you're officially…" eyebrow + heading (Brand Rep page) or the "how to claim" heading
    // (Unlock page) — neither is true/useful for an already-active rep whose details are withheld, so only the greeting stays.
    if (intro) {
      Array.from(intro.children).forEach((child) => {
        if (child !== greetingEl) child.hidden = true;
      });
    }
    let note = successPanel.querySelector('[data-withheld-note]');
    if (!note) {
      note = document.createElement('p');
      note.setAttribute('data-withheld-note', '');
      note.className = 'br-success__code-copy';
      if (intro && intro.parentNode) intro.parentNode.insertBefore(note, intro.nextSibling);
      else successPanel.appendChild(note);
    }
    // The theme's translation wins over the backend's English-only message.
    note.textContent = t('withheld-note', (data && typeof data.message === 'string' && data.message) ||
      "You're already an Ancarta Brand Rep. Your code details are in your welcome email, or sign in to your Ancarta account to open your Brand Rep dashboard.");
    note.hidden = false;
    if (formPanel) formPanel.hidden = true;
    successPanel.hidden = false;
    showSuccessFromTop();
    announce(t('withheld-announce', "You're already an Ancarta Brand Rep. Your details are in your welcome email."));
    publish('brand_rep_signup_complete', { source: captureSource(), backend: 'confirmed', recovered: true, withheld: true });
  }

  // Ruby's page (owner 2026-10-20): the welcome screen opens at the very top of the page, header and
  // all, like a new page. The theme scrolls inside .page-wrapper, so reset that as well as the window.
  // The Brand Rep page keeps scrolling to its success panel.
  function showSuccessFromTop() {
    if (!isUnlockFunnel) {
      successPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    for (let el = successPanel.parentElement; el; el = el.parentElement) {
      if (el.scrollTop > 0) el.scrollTop = 0;
    }
  }

  function showConfirmedSuccess(data) {
    if (!successPanel) return;
    if (data && data.detailsWithheld) {
      showWithheldRecovery(data);
      return;
    }

    const greetingEl = successPanel.querySelector('[ref="successGreeting"]');
    const codeLabelEl = successPanel.querySelector('[ref="codeLabel"]');
    const codeEl = successPanel.querySelector('[ref="successCode"]');
    const pendingNoteEl = successPanel.querySelector('[ref="codePendingNote"]');
    const bioTextEl = successPanel.querySelector('[ref="successBioText"]');
    const codeCopyComponent = successPanel.querySelector('[ref="codeCopyComponent"]');
    const bioCopyComponent = successPanel.querySelector('[ref="bioCopyComponent"]');
    const shareLinkCard = successPanel.querySelector('[ref="shareLinkCard"]');
    const shareLinkEl = successPanel.querySelector('[ref="successShareLink"]');
    const shareLinkCopyComponent = successPanel.querySelector('[ref="shareLinkCopyComponent"]');
    // Unlock funnel only (2026-09-13) — these refs don't exist on the plain
    // brand-rep-signup.liquid markup, so every lookup below is null there
    // and every block is skipped: zero behaviour change on /pages/brand-rep.
    // Never guesses/reconstructs the private code client-side — only ever
    // renders data.privateBogoCode, the exact value the backend's own
    // private BOGO orchestration resolved (see signup.js).
    // The Ruby welcome screen shows her code twice (owner 2026-10-26: under the hero and near the bottom), so the
    // block below runs once per copy.
    const privateCodeBands = Array.from(successPanel.querySelectorAll('[ref="unlockPrivateCode"]')).map((codeEl) => {
      const band = codeEl.closest('.welcome-code') || successPanel;
      return {
        privateCodeEl: codeEl,
        privateCodeLabelEl: band.querySelector('[ref="unlockPrivateCodeLabel"]'),
        privateCodePendingEl: band.querySelector('[ref="unlockPrivateCodePending"]'),
        privateCodeCopyComponent: band.querySelector('[ref="unlockPrivateCodeCopyComponent"]'),
      };
    });

    const bioText = data.bioText || buildBioText(data.firstName, data.code, data.shareUrl);

    // Existing Brand Rep recovery (2026-09-14): the backend returns the
    // exact same success shape for a recovered rep as a brand-new signup
    // (see ancarta-brand-rep-backend/src/signup.js's recoverExistingBrandRep),
    // only with `recoveredExistingRep: true` added — so this is the ONE
    // place that needs to know the difference, reusing the existing
    // greeting element rather than adding new markup.
    //
    // Copy (2026-09-15 redesign): never exposes the backend recovery
    // mechanic to her — no wording naming the fact that her details were
    // looked up/recovered. She just sees a warmer greeting, exactly as if
    // she'd always been able to pick up where she left off.
    if (greetingEl) {
      greetingEl.textContent = data.recoveredExistingRep
        ? (data.firstName ? t('welcome-back-name', 'Welcome back, {name}', { name: data.firstName }) : t('welcome-back', 'Welcome back'))
        : data.firstName
        ? t('welcome-name', 'Welcome, {name}.', { name: data.firstName })
        : t('welcome-generic', 'Welcome to Ancarta.');
    }
    // Only this confirmed path ever claims the code is live — the label's
    // static default ("YOUR PERSONAL CODE") stays accurate for the pending
    // fallback below, which never reaches this function.
    if (codeLabelEl) codeLabelEl.textContent = t('code-active', 'YOUR CODE IS ACTIVE');
    if (codeEl) codeEl.textContent = data.code;
    if (pendingNoteEl) pendingNoteEl.hidden = true;
    if (bioTextEl) bioTextEl.textContent = bioText;
    if (codeCopyComponent && data.code) codeCopyComponent.setAttribute('text-to-copy', data.code);
    if (bioCopyComponent) bioCopyComponent.setAttribute('text-to-copy', bioText);
    // Ruby welcome "a little tip" card (owner 2026-10-21): her share code in the bio line and the phone mock-up, her
    // first name / Instagram username in the mock-up, and the bio line as the copy text.
    if (isUnlockFunnel && data.code) {
      successPanel.querySelectorAll('[data-share-bio-code]').forEach((el) => { el.textContent = data.code; });
      const bioCopy = successPanel.querySelector('[data-share-bio-copy]');
      if (bioCopy) bioCopy.setAttribute('text-to-copy', `${bioCopy.getAttribute('data-bio-prefix') || '15% off @ancarta.co with my code'} ${data.code}`);
    }
    if (isUnlockFunnel) {
      // Name and username fill even before her share code exists. Her name as she typed it if the response has none;
      // the Instagram username she typed first, then the one saved with her welcome, then her private code.
      fillBioMock(successPanel, data.firstName || lastSubmittedFirstName, [lastSubmittedHandle, data.instagram, data.privateBogoCode]);
    }

    // The share-link card starts `hidden` in the markup — only reveal it
    // once a real redirect genuinely exists (data.shareUrl is only ever set
    // by the backend after ensureShareLinkRedirect actually succeeded).
    // Never fabricate a link from just the code if the backend didn't
    // confirm one — see signup.js's rollback/no-orphan-redirect design.
    if (data.shareUrl) {
      if (shareLinkEl) shareLinkEl.textContent = data.shareUrl.replace(/^https:\/\//, '');
      if (shareLinkCopyComponent) shareLinkCopyComponent.setAttribute('text-to-copy', data.shareUrl);
      if (shareLinkCard) shareLinkCard.hidden = false;
    } else if (shareLinkCard) {
      shareLinkCard.hidden = true;
    }

    // Unlock funnel only — see the ref lookups above. data.privateBogoCode
    // is only ever the real resolved code or null (never a placeholder —
    // see signup.js). A null code means private BOGO isn't active for this
    // signup (disabled or a genuine creation failure); the pending note
    // says so honestly rather than showing a broken/fabricated value, and
    // she can still shop the 3-item mechanic immediately regardless.
    privateCodeBands.forEach(({ privateCodeEl, privateCodeLabelEl, privateCodePendingEl, privateCodeCopyComponent }) => {
      if (privateCodeEl) {
        if (data.privateBogoCode) {
          // The Ruby welcome screen (2026-10-20) keeps its own "Your welcome code" label once a real code
          // is shown (data-static-label); every other state below still overwrites it honestly.
          if (privateCodeLabelEl) privateCodeLabelEl.textContent = privateCodeLabelEl.getAttribute('data-static-label') || t('discount-code-is', 'YOUR DISCOUNT CODE IS');
          privateCodeEl.textContent = data.privateBogoCode;
          privateCodeEl.hidden = false;
          if (privateCodePendingEl) privateCodePendingEl.hidden = true;
          if (privateCodeCopyComponent) {
            privateCodeCopyComponent.setAttribute('text-to-copy', data.privateBogoCode);
            privateCodeCopyComponent.hidden = false;
          }
        } else if (data.privateBogoWithheld) {
          // Existing-rep privacy (2026-09-20, DEV, EXISTING_REP_RESPONSE_MODE=public_only): the backend deliberately does
          // not return an existing rep's private code. Say where it is, never "being prepared" (that would be untrue).
          if (privateCodeLabelEl) privateCodeLabelEl.textContent = t('private-code-sent', 'YOUR PRIVATE CODE WAS SENT TO YOU IN YOUR WELCOME MESSAGE');
          privateCodeEl.hidden = true;
          if (privateCodePendingEl) privateCodePendingEl.hidden = true;
          if (privateCodeCopyComponent) privateCodeCopyComponent.hidden = true;
        } else {
          // Genuine creation failure (or disabled) — stays in the same
          // honest "being prepared" copy the markup already starts in
          // (see unlock-signup.liquid), never a fabricated code. No
          // separate "failed" message: this codebase never retries a
          // failed private BOGO automatically, so a distinct terminal
          // message would need a real retry path to be honest about —
          // out of scope here (backend mechanics are not being touched).
          if (privateCodeLabelEl) privateCodeLabelEl.textContent = t('discount-code-preparing', 'YOUR DISCOUNT CODE IS BEING PREPARED…');
          privateCodeEl.hidden = true;
          if (privateCodePendingEl) privateCodePendingEl.hidden = false;
          if (privateCodeCopyComponent) privateCodeCopyComponent.hidden = true;
        }
      }
    });

    applyCodeToShopLinks(successPanel, data.privateBogoCode);
    showCodeText(successPanel, data.privateBogoCode);
    // Phase 2: mark this browser session as a Brand Rep session for the cart
    // offer assistant (assets/brand-rep-cart-assistant.js), with HER code
    // only — session-scoped, never shared across visitors or devices.
    try {
      sessionStorage.setItem('ancartaBrandRepContext', JSON.stringify({ code: data.privateBogoCode || null, at: Date.now() }));
    } catch (error) {
      /* storage unavailable: the assistant falls back to Liquid-only behaviour */
    }

    if (formPanel) formPanel.hidden = true;
    successPanel.hidden = false;
    if (!data.restored) showSuccessFromTop();

    if (!isUnlockFunnel && data.code) {
      // Brand Rep signup page (owner 2026-10-28): remember her welcome on this device, like Ruby's page. A welcome
      // restored from her signed-in account is saved too, so this browser remembers her next time.
      if (!data.restored || data.fromAccount) {
        try {
          localStorage.setItem(BR_WELCOME_KEY, JSON.stringify({
            firstName: data.firstName || '',
            code: data.code,
            privateBogoCode: data.privateBogoCode || null,
            shareUrl: data.shareUrl || null,
            recoveredExistingRep: Boolean(data.recoveredExistingRep),
            savedAt: Date.now(),
          }));
        } catch (error) {
          /* storage unavailable: a refresh shows the form again, as before */
        }
      }
      const brReset = successPanel.querySelector('[data-br-welcome-reset]');
      if (brReset) {
        const named = brReset.getAttribute('data-named') || '';
        brReset.textContent = data.firstName && named ? named.replace('{name}', data.firstName) : brReset.getAttribute('data-plain') || '';
        brReset.hidden = false;
        // Restored from her signed-in account: "Not {name}?" SIGNS HER OUT, so the next person on this phone never sees
        // her codes; a separate link starts a deliberate new signup.
        if (data.fromAccount) {
          const namedAccount = brReset.getAttribute('data-named-account') || '';
          if (data.firstName && namedAccount) brReset.textContent = namedAccount.replace('{name}', data.firstName);
          const newSignup = successPanel.querySelector('[data-br-welcome-new-signup]');
          if (newSignup) {
            const params = new URLSearchParams(window.location.search);
            params.set('signup', '1');
            newSignup.setAttribute('href', window.location.pathname + '?' + params.toString());
            newSignup.hidden = false;
          }
        }
        brReset.onclick = () => {
          clearSavedWelcome();
          if (data.fromAccount) {
            window.location.assign(brReset.getAttribute('data-signout-href') || '/account/logout');
            return;
          }
          window.location.reload();
        };
      }
    }

    if (isUnlockFunnel && data.privateBogoCode) {
      // A welcome restored from her signed-in account is saved too, so this browser remembers her next time.
      if (!data.restored || data.fromAccount) {
        try {
          localStorage.setItem(WELCOME_KEY, JSON.stringify({
            firstName: data.firstName || '',
            code: data.code || '',
            privateBogoCode: data.privateBogoCode,
            shareUrl: data.shareUrl || null,
            recoveredExistingRep: Boolean(data.recoveredExistingRep),
            instagram: socialHandle(data.instagram || lastSubmittedHandle, 30),
            savedAt: Date.now(),
          }));
        } catch (error) {
          /* storage unavailable: a refresh shows the form again, as before */
        }
      }
      const reset = successPanel.querySelector('[data-welcome-reset]');
      if (reset) {
        const named = reset.getAttribute('data-named') || '';
        const plain = reset.getAttribute('data-plain') || '';
        reset.textContent = data.firstName && named ? named.replace('{name}', data.firstName) : plain;
        reset.hidden = false;
        // Restored from her signed-in account (review fix 2026-10-21): "Not {name}?" SIGNS HER OUT. Only opening the
        // form would leave her signed in, so a reload (or the next person on this phone) would see her codes again.
        const footSignout = successPanel.querySelector('[data-welcome-signout]');
        const signoutHref = footSignout ? footSignout.getAttribute('href') : '/account/logout';
        if (data.fromAccount) {
          const namedAccount = reset.getAttribute('data-named-account') || '';
          if (data.firstName && namedAccount) reset.textContent = namedAccount.replace('{name}', data.firstName);
          if (footSignout) footSignout.hidden = true; // one sign-out control, not two
          const newSignup = successPanel.querySelector('[data-welcome-new-signup]');
          if (newSignup) {
            const params = new URLSearchParams(window.location.search);
            params.set('signup', '1');
            newSignup.setAttribute('href', window.location.pathname + '?' + params.toString());
            newSignup.hidden = false;
          }
        }
        reset.onclick = () => {
          clearSavedWelcome();
          if (data.fromAccount) {
            window.location.assign(signoutHref);
            return;
          }
          window.location.reload();
        };
      }
    }
    // The Unlock funnel's success panel carries the private-code refs
    // above; the plain Brand Rep page's doesn't — so this stays accurate
    // for a screen-reader user on either page without needing to know
    // which page it's on any other way.
    announce(
      data.recoveredExistingRep
        ? t('announce-welcome-back', 'Welcome back — your 2 free items are unlocked.')
        : privateCodeBands.length
        ? t('announce-unlocked', 'Your private offer is unlocked.')
        : t('announce-official', "You're officially an Ancarta Brand Rep.")
    );

    // A screen restored after a refresh is not a new signup: no signup analytics again.
    if (data.restored) return;
    publish('brand_rep_signup_complete', { source: captureSource(), backend: 'confirmed', recovered: Boolean(data.recoveredExistingRep) });
    // "created" here means "confirmed active", matching the recovery path
    // too — recoveredExistingRep can report a code that already existed
    // long before this visit; that's still a genuinely active code, not a
    // duplicate creation.
    publish('brand_rep_code_created', { code: data.code });
    if (data.privateBogoCode) publish('unlock_private_code_revealed', { recovered: Boolean(data.recoveredExistingRep) });
  }

  /**
   * Phase 1 (2026-09-25): upgrades the Unlock success CTAs (BUILD MY SET /
   * SHOP BOTTOMS) to Shopify's native /discount/{code}?redirect=… route so
   * HER private code is applied to HER browser session automatically. Only
   * ever uses the real code the backend returned for this signup; a null
   * code (not active, or a withheld existing-rep response) leaves the plain
   * collection links untouched. Relative in-store paths only.
   */
  function applyCodeToShopLinks(panel, privateBogoCode) {
    const links = panel.querySelectorAll('[data-unlock-code-link]');
    links.forEach((link) => {
      const path = link.getAttribute('data-unlock-code-link') || '';
      if (!path.startsWith('/') || path.startsWith('//')) return;
      link.setAttribute(
        'href',
        privateBogoCode ? `${shopRoot()}discount/${encodeURIComponent(privateBogoCode)}?redirect=${encodeURIComponent(path)}` : path
      );
    });
  }

  /**
   * Ruby welcome screen (2026-10-20): fills every [data-unlock-code-text] with HER real code and
   * reveals the [data-unlock-code-line] notes ("Your code … is applied automatically"). Those notes
   * start hidden, so with no code (pending, withheld, no JS) they never claim a code is applied.
   */
  function showCodeText(panel, privateBogoCode) {
    panel.querySelectorAll('[data-unlock-code-text]').forEach((el) => {
      el.textContent = privateBogoCode || '';
    });
    panel.querySelectorAll('[data-unlock-code-line]').forEach((el) => {
      el.hidden = !privateBogoCode;
    });
  }

  function prepareNamesFromFullName() {
    const fullName = fullNameInput ? fullNameInput.value.trim() : '';
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    if (firstNameInput) firstNameInput.value = firstName;
    if (lastNameInput) lastNameInput.value = lastName;
    return firstName;
  }

  // Ruby welcome screen: a refresh (or reopening Ruby's link) brings her saved welcome screen back.
  // Runs before the backend branch below, which returns early.
  // Returning Brand Rep (owner-approved 2026-10-21), in order:
  //  1. ?signup=1 always shows the form (deliberate new signup, e.g. a friend on her phone);
  //  2. signed in to her Shopify account as a rep: her welcome from her own customer record (any browser/device);
  //  3. her welcome saved on this browser (since 2026-10-20).
  // Both are reads of existing data: no signup request, no new code, no signup analytics (data.restored).
  const forceSignupForm = new URLSearchParams(window.location.search).has('signup');
  const accountWelcome = isUnlockFunnel && !forceSignupForm ? readAccountWelcome() : null;
  if (accountWelcome && successPanel && successPanel.hidden) {
    successPanel.setAttribute('data-welcome-restored', '');
    successPanel.setAttribute('data-welcome-account', '');
    showConfirmedSuccess(Object.assign({}, accountWelcome, { success: true, restored: true, fromAccount: true, recoveredExistingRep: true }));
  }
  if (isUnlockFunnel && successPanel && successPanel.hidden && !forceSignupForm) {
    const saved = readSavedWelcome();
    if (saved) {
      successPanel.setAttribute('data-welcome-restored', '');
      showConfirmedSuccess(Object.assign({}, saved, { success: true, restored: true }));
    }
  }
  if (!isUnlockFunnel && successPanel && successPanel.hidden && !forceSignupForm) {
    // Brand Rep signup page (owner 2026-10-28), same order as Ruby's page: her signed-in account first, then this browser.
    // A refresh, coming back to the link, or signing in on another phone shows her welcome again.
    const accountBr = readBrandRepAccountWelcome();
    if (accountBr) successPanel.setAttribute('data-welcome-account', '');
    const saved = accountBr
      ? Object.assign({}, accountBr, { fromAccount: true, recoveredExistingRep: true })
      : readSavedBrandRepWelcome();
    if (saved) {
      successPanel.setAttribute('data-welcome-restored', '');
      showConfirmedSuccess(Object.assign({}, saved, { success: true, restored: true }));
      // Her welcome sits below the page's intro sections: take her to it, unless the browser already restored her place.
      requestAnimationFrame(() => { if (window.scrollY < 50) successPanel.scrollIntoView({ block: 'start' }); });
    }
  }
  if (!isUnlockFunnel) {
    // Brand Rep signup page (owner 2026-10-28), same as Ruby's page: login returns her here, signing out forgets her
    // welcome on this device, and the back button after signing out never shows a cached welcome.
    form.querySelectorAll('[data-access-invitation]').forEach((link) => { link.href = loginUrlForThisPage(); });
    form.querySelectorAll('[data-welcome-signout]').forEach((link) => link.addEventListener('click', clearSavedWelcome));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && successPanel && !successPanel.hidden) window.location.reload();
    });
  }
  if (isUnlockFunnel) {
    // Login returns her to this exact page, ?src=ruby and all (Liquid renders a path-only fallback).
    form.querySelectorAll('[data-access-invitation]').forEach((link) => { link.href = loginUrlForThisPage(); });
    // Signing out (switching accounts) also forgets her welcome on this device.
    form.querySelectorAll('[data-welcome-signout]').forEach((link) => link.addEventListener('click', clearSavedWelcome));
    // Back button after signing out / "Not you?": never show a welcome screen from the browser's page cache; reload so
    // the page reflects who is signed in now (review fix 2026-10-21).
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && successPanel && !successPanel.hidden) window.location.reload();
    });
  }

  if (apiEndpoint) {
    // Real backend path. The button is type="button" (see the Liquid
    // template) whenever apiEndpoint is configured, specifically so this
    // never has to cancel a native form submission — live testing showed a
    // real trusted click on a type="submit" button could occasionally
    // reach Shopify's own native `{% form 'customer' %}` handling (and its
    // bot challenge) despite this handler's own preventDefault() call,
    // even though that call is provably synchronous and unconditional on
    // this path. Removing the native submission from the equation entirely
    // removes whatever timing this depended on, rather than trying to win
    // a race against it.
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        if (isSubmitting) return; // guards double-click/repeat-tap, same as before

        // reportValidity() gives the same native required-field UI a
        // type="submit" button would have produced for free.
        if (!form.reportValidity()) return;

        if (codeInput && RESERVED_CODES.includes(codeInput.value)) {
          updateCodeStatus();
          codeInput.focus();
          return;
        }

        prepareNamesFromFullName();
        submitToRealBackend(collectPayload());
      });
    }
    return;
  }

  // No backend deployed — native-form fallback (unchanged behaviour): the
  // button stays a real type="submit", so this listener's job is only to
  // prepare the hidden fields/session snapshot before Shopify's own native
  // `{% form 'customer' %}` processing takes over.
  form.addEventListener('submit', (event) => {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }

    if (codeInput && RESERVED_CODES.includes(codeInput.value)) {
      event.preventDefault();
      updateCodeStatus();
      codeInput.focus();
      return;
    }

    const firstName = prepareNamesFromFullName();
    const igSlug = socialHandle(instagramInput ? instagramInput.value : '', 30);
    const ttSlug = slugify(tiktokInput ? tiktokInput.value : '', 30);
    const code = codeInput ? sanitizeCode(codeInput.value) : '';
    const source = slugify(captureSource(), 30);

    const tags = ['brand-rep-pending'];
    if (igSlug) tags.push('br-ig-' + igSlug);
    if (ttSlug) tags.push('br-tt-' + ttSlug);
    if (code) tags.push('br-code-' + code.toLowerCase());
    if (source) tags.push('br-source-' + source);
    if (tagsInput) tagsInput.value = tags.join(',');

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        firstName,
        code,
        instagram: instagramInput ? instagramInput.value : '',
        email: emailInput ? emailInput.value.trim() : '',
      }));
    } catch (error) {
      /* non-fatal: success screen falls back to generic copy */
    }

    setLoading(true);
  });
}

/**
 * PENDING success state — only reached via the native-form fallback path
 * (see the doc comment at the top of this file). Deliberately distinct
 * from showConfirmedSuccess: this never removes the "Activating" badge or
 * the pending note, and fires brand_rep_code_requested, not
 * brand_rep_code_created.
 */
function hydratePendingSuccessState() {
  const successPanel = document.querySelector('[data-state="success"]');
  if (!successPanel || successPanel.hasAttribute('hidden')) return;
  // A welcome screen restored from her saved details (initSignupForm) is already complete.
  if (successPanel.hasAttribute('data-welcome-restored')) return;

  let snapshot = null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) snapshot = JSON.parse(raw);
    sessionStorage.removeItem(SESSION_KEY);
  } catch (error) {
    snapshot = null;
  }

  const greetingEl = successPanel.querySelector('[ref="successGreeting"]');
  const codeEl = successPanel.querySelector('[ref="successCode"]');
  const bioTextEl = successPanel.querySelector('[ref="successBioText"]');
  const codeCopyComponent = successPanel.querySelector('[ref="codeCopyComponent"]');
  const bioCopyComponent = successPanel.querySelector('[ref="bioCopyComponent"]');

  const firstName = snapshot && snapshot.firstName ? snapshot.firstName : '';
  const code = snapshot && snapshot.code ? snapshot.code : '';

  if (greetingEl) greetingEl.textContent = firstName ? t('welcome-name', 'Welcome, {name}.', { name: firstName }) : t('welcome-generic', 'Welcome to Ancarta.');
  if (codeEl) codeEl.textContent = code || t('pending', 'Pending');
  fillBioMock(successPanel, firstName, [snapshot && snapshot.instagram]);

  // No literal fallback like "YOURCODE" here — an unconfirmed code truly
  // isn't known yet, so buildBioText with an empty code just trails off
  // after the em dash rather than showing something that reads as real.
  const bioText = buildBioText(firstName, code);
  if (bioTextEl) bioTextEl.textContent = bioText;
  if (codeCopyComponent && code) codeCopyComponent.setAttribute('text-to-copy', code);
  if (bioCopyComponent) bioCopyComponent.setAttribute('text-to-copy', bioText);

  publish('brand_rep_signup_complete', {
    source: (function () {
      try {
        return sessionStorage.getItem(SOURCE_KEY) || 'direct';
      } catch (error) {
        return 'direct';
      }
    }()),
    backend: 'pending_fallback',
  });

  if (code) {
    /*
      "requested", not "created": no real Shopify discount exists yet at
      this point (see the architecture note in brand-rep-signup.liquid) —
      naming this event brand_rep_code_created would misrepresent what
      actually happened.
    */
    publish('brand_rep_code_requested', { code_requested: code });
  }
}

/**
 * Mobile-only sticky "BECOME A BRAND REP" CTA (markup lives in
 * sections/brand-rep-hero.liquid, [ref="brStickyCta"], starts [hidden]).
 * Desktop is excluded entirely by CSS (`display: none !important` ≥750px)
 * — this only ever toggles the [hidden] attribute, never layout, so there
 * is no layout shift either way.
 *
 * Visible only once ALL of:
 *  - the visitor has scrolled past the hero IMAGE specifically (its box has
 *    scrolled fully above the viewport top) — not the whole hero section,
 *    which also includes the copy block above the image and a padding
 *    strip below it; watching the image/frame itself (rather than the
 *    section) means the CTA can appear as soon as the last model is off
 *    screen, without waiting for that extra padding to clear too
 *  - the signup section has never been reached yet (hides as soon as it
 *    enters view, and — once reached — stays hidden even if she later
 *    scrolls past it into the footer, since she's already seen the real CTA)
 *  - the success state is NOT being shown (checked via the success panel's
 *    own [hidden] attribute — the same flag showConfirmedSuccess/
 *    hydratePendingSuccessState already flip)
 *  - Shopify's own cookie-consent banner (#shopify-pc__banner) is not
 *    currently on screen, so the two fixed bottom bars never stack
 */
function initStickyCta() {
  const sticky = document.querySelector('[ref="brStickyCta"]');
  // The mobile hero photo's own frame (sections/brand-rep-hero.liquid),
  // not the whole <section id="brand-rep-hero">, which also contains the
  // copy block above it and a padding strip below it — see the doc
  // comment above. Falls back to the whole hero section if the frame
  // isn't found for some reason (e.g. a future markup change), so this
  // never silently breaks the feature.
  const heroImageFrame = document.querySelector('.br-hero__frame-inner--mobile') || document.getElementById('brand-rep-hero');
  const signupSection = document.getElementById('brand-rep-signup');
  if (!sticky || !heroImageFrame || !signupSection) return;

  const successPanel = signupSection.querySelector('[data-state="success"]');

  const state = {
    pastHero: false,
    signupVisible: true,
    // Once she's scrolled far enough to reach the signup section at least
    // once, she's already seen the real CTA — the sticky shortcut has done
    // its job and shouldn't reappear if she keeps scrolling into the
    // footer past it.
    reachedSignup: false,
    successVisible: successPanel ? !successPanel.hidden : false,
  };

  function isCookieBannerVisible() {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner) return false;
    const style = window.getComputedStyle(banner);
    return style.display !== 'none' && style.visibility !== 'hidden' && banner.offsetHeight > 0;
  }

  function render() {
    const shouldShow = state.pastHero && !state.signupVisible && !state.successVisible && !isCookieBannerVisible();
    // Guard against a redundant write: sticky itself lives under document.body,
    // and the body-wide MutationObserver below watches 'hidden' (among other
    // attributes) so it can catch the cookie banner's visibility changing in
    // place. Writing sticky.hidden unconditionally would re-trigger that same
    // observer on every render() call — including calls the observer itself
    // caused — creating an infinite self-triggering mutation loop. Only
    // writing when the value actually changes breaks that loop.
    if (sticky.hidden !== !shouldShow) sticky.hidden = !shouldShow;
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        state.pastHero = entry.boundingClientRect.bottom < 0;
        render();
      });
    }, { threshold: 0 }).observe(heroImageFrame);

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) state.reachedSignup = true;
        state.signupVisible = state.reachedSignup ? true : entry.isIntersecting;
        render();
      });
    }, { threshold: 0.1 }).observe(signupSection);
  }

  if (successPanel) {
    new MutationObserver(() => {
      state.successVisible = !successPanel.hidden;
      render();
    }).observe(successPanel, { attributes: true, attributeFilter: ['hidden'] });
  }

  // Shopify's privacy-consent script may insert/remove #shopify-pc__banner
  // outright, OR toggle its visibility in place via a style/class change on
  // the same node (its own future implementation isn't guaranteed to pick
  // one or the other) — childList alone would miss the second case, so this
  // also watches style/class/hidden attribute changes anywhere under body.
  // attributeFilter keeps it cheap: it only fires for those three
  // attributes, not every attribute mutation on the page.
  new MutationObserver(render).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });

  render();
}

function initAnalyticsDelegation() {
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-br-analytics]');
    if (!trigger) return;
    trigger.getAttribute('data-br-analytics').split(',').forEach((eventName) => {
      publish(eventName.trim(), {});
    });
  });

  document.querySelectorAll('.br-success__copy-button').forEach((button) => {
    button.addEventListener('click', () => {
      const isSecondary = button.classList.contains('br-success__copy-button--secondary');
      publish(isSecondary ? 'brand_rep_bio_copied' : 'brand_rep_code_copied', {});
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ready);
} else {
  ready();
}

function ready() {
  if (document.getElementById('brand-rep-hero') || document.getElementById('brand-rep-signup')) {
    publish('brand_rep_page_view', {});
  }
  initSignupForm();
  hydratePendingSuccessState();
  initAnalyticsDelegation();
  initStickyCta();
}

/**
 * Ruby welcome screen (2026-10-20): remember which of Ruby's outfits she tapped. Her SHOP THIS LOOK
 * link goes through /discount/{code}?redirect=/pages/shop-the-look, and Shopify drops the ?look=
 * part of that redirect, so assets/shop-the-look.js reads the choice from this tab's sessionStorage.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-look-id]') : null;
  if (!link) return;
  try {
    sessionStorage.setItem('ancartaLook', link.getAttribute('data-look-id'));
  } catch (error) {
    /* storage unavailable: the look page falls back to ?look= or the first look */
  }
});

/**
 * Ruby welcome screen (2026-10-20): country / currency picker under the shipping line. A normal
 * localization submit reloads the page, and the welcome screen only exists client-side after her
 * signup, so it would be lost. Instead: post the localization form in the background, re-render
 * this section in the new country (Section Rendering API), swap in the price blocks, and route the
 * new shop links through her code again. Any failure falls back to the normal submit.
 */
document.addEventListener('change', async (event) => {
  const select = event.target instanceof Element ? event.target.closest('[data-welcome-locale-select]') : null;
  if (!select || !select.form) return;
  const form = select.form;
  // Only ever the localization form (never the signup form around the welcome screen).
  if (!/\/localization(\b|\/|$)/.test(new URL(form.action, window.location.href).pathname)) return;
  const panel = select.closest('[data-state="success"]');
  const sectionEl = select.closest('[id^="shopify-section-"]');
  const fallback = () => form.submit();
  if (!panel || !sectionEl || typeof fetch !== 'function') {
    fallback();
    return;
  }
  const body = new FormData(form);
  select.disabled = true;
  try {
    await fetch(form.action, { method: 'POST', body, redirect: 'manual', credentials: 'same-origin' });
    const sectionId = sectionEl.id.replace('shopify-section-', '');
    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`section ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    let swapped = 0;
    ['.welcome-value', '.welcome-looks__rail'].forEach((selector) => {
      const fresh = doc.querySelector(selector);
      const old = panel.querySelector(selector);
      if (fresh && old) {
        old.replaceWith(document.importNode(fresh, true));
        swapped += 1;
      }
    });
    if (!swapped) throw new Error('nothing to swap');
    let code = '';
    try {
      const context = JSON.parse(sessionStorage.getItem('ancartaBrandRepContext') || 'null');
      code = (context && context.code) || '';
    } catch (error) {
      code = '';
    }
    if (code) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      panel.querySelectorAll('[data-unlock-code-link]').forEach((link) => {
        const path = link.getAttribute('data-unlock-code-link') || '';
        if (path.startsWith('/') && !path.startsWith('//')) {
          link.setAttribute('href', `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(path)}`);
        }
      });
    }
    const fresh = panel.querySelector('[data-welcome-locale-select]');
    if (fresh) fresh.focus();
  } catch (error) {
    fallback();
  }
});

